import {
 BaseQueryApi,
 FetchArgs,
 fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { DetectOSAndBrowser } from "./DetectBroweser";
import { setCredentials, logout } from "../slices/authSlice"; // Ensure you import your auth actions
import { LocalStorage } from "../../types/local_storage.enum";
import { toast } from "react-toastify";
import { BACKEND_URL } from "../../utils/env.config";

const BASE_URL = BACKEND_URL;

export const baseQuery = fetchBaseQuery({
 baseUrl: BASE_URL,
 prepareHeaders: (headers, { getState }) => {
  const token = localStorage.getItem(LocalStorage.TOKEN);
  if (token) {
   headers.set("Authorization", `Bearer ${token}`);
  }
  headers.set("Content-Type", "application/json");
  headers.set("Client-OS", DetectOSAndBrowser().OS);
  headers.set("Client-Browser", DetectOSAndBrowser().Browser);
  return headers;
 },
});
// Wait for internet to be back online

const waitForOnline = (): Promise<void> => {
 return new Promise((resolve) => {
  if (navigator.onLine) {
   resolve();
  } else {
   const handleOnline = () => {
    window.removeEventListener("online", handleOnline);
    resolve();
   };
   window.addEventListener("online", handleOnline);
  }
 });
};

function waitForInternet(): Promise<void> {
 return new Promise((resolve) => {
  const interval = setInterval(async () => {
   if (await hasInternet()) {
    clearInterval(interval);
    resolve();
   }
  }, 3000); // check every 3 seconds
 });
}

export async function hasInternet(
 url: string = "https://www.google.com/favicon.ico"
): Promise<boolean> {
 try {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout

  await fetch(url, {
   method: "HEAD",
   mode: "no-cors",
   signal: controller.signal,
  });

  clearTimeout(timeoutId);
  return true;
 } catch {
  return false;
 }
}

export const baseQueryWithReauth = async (
 args: string | FetchArgs,
 api: BaseQueryApi,
 extraOptions: {}
) => {
 let attempt = 0;
 const maxRetries = 3;

 const executeQueryWithRetry = async (): Promise<any> => {
  let result = await baseQuery(args, api, extraOptions);

  const isOnline = await hasInternet();
  const toasted = localStorage.getItem(LocalStorage.OFFLINE);
  while (result.error?.status === "FETCH_ERROR" && attempt < maxRetries) {
   attempt++;

   await new Promise((resolve) => setTimeout(resolve, attempt * 1000)); // exponential backoff
   result = await baseQuery(args, api, extraOptions);
  }

  // Still a network error after retries, wait for connection
  if (result.error?.status === "FETCH_ERROR" && !isOnline && !toasted) {
   //    localStorage.setItem(LocalStorage.OFFLINE, "true");
   //    const toastId = toast.info(
   //     "You are offline. Waiting for connection to retry...",
   //     { autoClose: false }
   //    );
   await waitForInternet();
   //    toast.dismiss(toastId);
   //    localStorage.removeItem(LocalStorage.OFFLINE);
   //    toast.success("Back online. Retrying request...");
   result = await baseQueryWithReauth(args, api, extraOptions);
  }

  return result;
 };

 let result = await executeQueryWithRetry();

 if (result.error && result.error.status === 401) {
  const refreshToken = localStorage.getItem(LocalStorage.REFRESH_TOKEN);

  if (!refreshToken) {
   toast.error("Session expired.Please login");
   api.dispatch(logout());
   return result;
  }

  const refreshResult = await baseQuery(
   {
    url: "Auth/RefreshToken",
    method: "POST",
    body: {
     RefreshToken: refreshToken,
     Token: localStorage.getItem(LocalStorage.TOKEN),
    },
   },
   api,
   extraOptions
  );

  if (refreshResult.data) {
   const refreshedData = refreshResult.data as {
    token: string;
    refreshToken: string;
   };
   api.dispatch(setCredentials(refreshedData));
   localStorage.setItem(LocalStorage.TOKEN, refreshedData.token);
   localStorage.setItem(LocalStorage.REFRESH_TOKEN, refreshedData.refreshToken);

   result = await baseQuery(args, api, extraOptions);
  } else {
   toast.error("Session Expired, Please Login Again");
   api.dispatch(logout());
  }
 } else if (result.error && result.error.status === 500) {
  toast.error("Server error. Please try again later.");
  return { error: { status: "SERVER_ERROR", message: "Server error" } };
 } else if (
  result.error &&
  "error" in result.error &&
  typeof result.error.error === "string" &&
  result.error.error.includes("Abort")
 ) {
  return { error: { status: "ABORTED", message: "Request aborted" } };
 } else if (result.error?.status === "FETCH_ERROR") {
  toast.error("Please check your internet connection.", { autoClose: false });
  return { error: { status: "NETWORK_ERROR", message: "Network error" } };
 } else if (result.error && typeof result.error.data === "string") {
  toast.error(result.error.data as string);
  return {
   error: { status: "BAD_REQUEST", message: result.error.data as string },
  };
 } else if (result.error) {
  return {
   error: {
    status: "UNKNOWN_ERROR",
    data: result.error.data,
    message: "Unexpected error",
   },
  };
 }

 return result;
};
